import latinRegularUrl from "@expo-google-fonts/noto-sans/400Regular/NotoSans_400Regular.ttf";
import latinBoldUrl from "@expo-google-fonts/noto-sans/700Bold/NotoSans_700Bold.ttf";
import georgianRegularUrl from "@expo-google-fonts/noto-sans-georgian/400Regular/NotoSansGeorgian_400Regular.ttf";
import georgianBoldUrl from "@expo-google-fonts/noto-sans-georgian/700Bold/NotoSansGeorgian_700Bold.ttf";
import arabicRegularUrl from "@expo-google-fonts/noto-sans-arabic/400Regular/NotoSansArabic_400Regular.ttf";
import arabicBoldUrl from "@expo-google-fonts/noto-sans-arabic/700Bold/NotoSansArabic_700Bold.ttf";
import hebrewRegularUrl from "@expo-google-fonts/noto-sans-hebrew/400Regular/NotoSansHebrew_400Regular.ttf";
import hebrewBoldUrl from "@expo-google-fonts/noto-sans-hebrew/700Bold/NotoSansHebrew_700Bold.ttf";
import japaneseRegularUrl from "@expo-google-fonts/noto-sans-jp/400Regular/NotoSansJP_400Regular.ttf";
import japaneseBoldUrl from "@expo-google-fonts/noto-sans-jp/700Bold/NotoSansJP_700Bold.ttf";
import koreanRegularUrl from "@expo-google-fonts/noto-sans-kr/400Regular/NotoSansKR_400Regular.ttf";
import koreanBoldUrl from "@expo-google-fonts/noto-sans-kr/700Bold/NotoSansKR_700Bold.ttf";
import chineseRegularUrl from "@expo-google-fonts/noto-sans-sc/400Regular/NotoSansSC_400Regular.ttf";
import chineseBoldUrl from "@expo-google-fonts/noto-sans-sc/700Bold/NotoSansSC_700Bold.ttf";
import "./ats-cv-builder.css";
import {
  ATS_CV_LIMITS,
  analyzeCv,
  buildCvBlocks,
  cvFontFamilies,
  isRtlText,
  moveListItem,
  normalizeCvFileName,
  normalizeCvTemplate,
  validateCvForExport
} from "./ats-cv-core.js";
import { createCvDocx, createCvPdf } from "./ats-cv-export.js";
import { createOperationResult } from "./operation-result.js";

const FONT_URLS = Object.freeze({
  latin: [latinRegularUrl, latinBoldUrl],
  georgian: [georgianRegularUrl, georgianBoldUrl],
  arabic: [arabicRegularUrl, arabicBoldUrl],
  hebrew: [hebrewRegularUrl, hebrewBoldUrl],
  japanese: [japaneseRegularUrl, japaneseBoldUrl],
  korean: [koreanRegularUrl, koreanBoldUrl],
  chinese: [chineseRegularUrl, chineseBoldUrl]
});

const root = document.querySelector("[data-ats-cv-builder]");
if (root) initialize();

function initialize() {
  const form = document.getElementById("cvForm");
  const workspace = document.getElementById("cvWorkspace");
  const status = document.getElementById("cvStatus");
  const experienceEntries = document.getElementById("experienceEntries");
  const educationEntries = document.getElementById("educationEntries");
  const preview = document.getElementById("cvPreview");
  const checks = document.getElementById("cvChecks");
  const score = document.getElementById("cvScore");
  const keywordResults = document.getElementById("keywordResults");
  const createButton = document.getElementById("createCv");
  const format = document.getElementById("cvFormat");
  const fileName = document.getElementById("cvFileName");
  const pageSize = document.getElementById("cvPageSize");
  const templateInputs = Array.from(root.querySelectorAll('input[name="cvTemplate"]'));
  const resetBuilder = () => {
    form.reset();
    for (const entry of Array.from(experienceEntries.querySelectorAll("[data-experience-entry]")).slice(1)) entry.remove();
    for (const entry of Array.from(educationEntries.querySelectorAll("[data-education-entry]")).slice(1)) entry.remove();
    format.value = "docx";
    pageSize.value = "A4";
    fileName.value = "ats-cv.docx";
    for (const input of templateInputs) input.checked = input.value === "classic";
  };
  const operationResult = createOperationResult(root, workspace, { onReset: resetBuilder });
  let busy = false;
  const fontPromises = new Map();

  const showStatus = (message, kind = "error") => {
    status.textContent = message;
    status.dataset.kind = kind;
    status.hidden = !message;
    if (message && kind === "error") window.toolboxAnalytics?.track("tool_error", { error_category: "validation" });
  };

  const setBusy = value => {
    busy = value;
    createButton.textContent = value ? "Creating CV…" : "Create ATS CV";
    for (const element of form.querySelectorAll("button, input, textarea, select")) element.disabled = value;
    for (const element of root.querySelectorAll(".cv-export-card input, .cv-export-card select, .cv-export-card button")) element.disabled = value;
    if (!value) {
      for (const current of form.querySelectorAll('[data-field="current"]:checked')) {
        const endDate = current.closest("[data-experience-entry]")?.querySelector('[data-field="endDate"]');
        if (endDate) endDate.disabled = true;
      }
      renumberEntries(experienceEntries, "[data-experience-entry]");
      renumberEntries(educationEntries, "[data-education-entry]");
    }
  };

  const collectCv = () => ({
    personal: {
      name: value("cvName"),
      targetTitle: value("cvTargetTitle"),
      email: value("cvEmail"),
      phone: value("cvPhone"),
      location: value("cvLocation"),
      linkedin: value("cvLinkedin"),
      portfolio: value("cvPortfolio"),
      summary: value("cvSummary")
    },
    experience: collectEntries(experienceEntries, "[data-experience-entry]"),
    education: collectEntries(educationEntries, "[data-education-entry]"),
    skills: value("cvSkills"),
    certifications: value("cvCertifications"),
    languages: value("cvLanguages")
  });

  const selectedTemplate = () => normalizeCvTemplate(templateInputs.find(input => input.checked)?.value);

  const update = () => {
    const cv = collectCv();
    const analysis = analyzeCv(cv, value("jobDescription"));
    preview.dataset.template = selectedTemplate();
    renderPreview(preview, buildCvBlocks(cv));
    renderChecks(checks, score, analysis);
    renderKeywords(keywordResults, analysis.keywordMatch);
  };

  const addEntry = (container, templateId, selector) => {
    if (container.querySelectorAll(selector).length >= (selector.includes("experience") ? ATS_CV_LIMITS.experienceEntries : ATS_CV_LIMITS.educationEntries)) {
      showStatus(`The maximum number of ${selector.includes("experience") ? "roles" : "education entries"} has been reached.`);
      return;
    }
    const fragment = document.getElementById(templateId).content.cloneNode(true);
    container.append(fragment);
    renumberEntries(container, selector);
    container.querySelector(`${selector}:last-child input`)?.focus();
    update();
  };

  document.getElementById("addExperience").addEventListener("click", () =>
    addEntry(experienceEntries, "experienceEntryTemplate", "[data-experience-entry]"));
  document.getElementById("addEducation").addEventListener("click", () =>
    addEntry(educationEntries, "educationEntryTemplate", "[data-education-entry]"));

  form.addEventListener("click", event => {
    const move = event.target.closest("[data-move-entry]");
    if (move && !busy) {
      const entry = move.closest("[data-experience-entry], [data-education-entry]");
      const container = entry.parentElement;
      const isExperience = entry.hasAttribute("data-experience-entry");
      const selector = isExperience ? "[data-experience-entry]" : "[data-education-entry]";
      const entries = Array.from(container.querySelectorAll(selector));
      const offset = move.dataset.moveEntry === "up" ? -1 : 1;
      const reordered = moveListItem(entries, entries.indexOf(entry), offset);
      if (reordered.every((item, index) => item === entries[index])) return;
      container.append(...reordered);
      renumberEntries(container, selector);
      const position = reordered.indexOf(entry) + 1;
      showStatus(`${isExperience ? "Role" : "Education entry"} moved to position ${position} of ${reordered.length}.`, "success");
      move.focus();
      update();
      return;
    }
    const remove = event.target.closest("[data-remove-entry]");
    if (!remove || busy) return;
    const entry = remove.closest("[data-experience-entry], [data-education-entry]");
    const container = entry.parentElement;
    const selector = entry.hasAttribute("data-experience-entry") ? "[data-experience-entry]" : "[data-education-entry]";
    entry.remove();
    renumberEntries(container, selector);
    update();
  });

  form.addEventListener("input", () => {
    showStatus("");
    update();
  });
  form.addEventListener("change", event => {
    if (event.target.matches('[data-field="current"]')) {
      const endDate = event.target.closest("[data-experience-entry]")?.querySelector('[data-field="endDate"]');
      if (endDate) endDate.disabled = event.target.checked || busy;
    }
    update();
  });

  format.addEventListener("change", () => {
    fileName.value = normalizeCvFileName(fileName.value, format.value);
  });
  for (const input of templateInputs) input.addEventListener("change", update);

  createButton.addEventListener("click", async () => {
    if (busy) return;
    const cv = collectCv();
    const errors = validateCvForExport(cv);
    if (errors.length) {
      showStatus(errors.join(" "));
      status.focus?.();
      return;
    }
    setBusy(true);
    showStatus("");
    try {
      const selectedFormat = format.value === "pdf" ? "pdf" : "docx";
      const exportSettings = { pageSize: pageSize.value, template: selectedTemplate() };
      const bytes = selectedFormat === "pdf"
        ? await createCvPdf(cv, exportSettings, await loadFonts(cv))
        : createCvDocx(cv, exportSettings);
      const outputName = normalizeCvFileName(fileName.value, selectedFormat);
      const type = selectedFormat === "pdf"
        ? "application/pdf"
        : "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      operationResult.show({
        blob: new Blob([bytes], { type }),
        fileName: outputName,
        title: "Your ATS CV is ready",
        message: `Your ${selectedFormat.toUpperCase()} CV was created with a single-column, text-based structure.`,
        details: [
          "Review names, dates, links, and claims before applying.",
          "Application systems differ, so always follow the employer’s requested file format."
        ],
        downloadLabel: `Download ${selectedFormat.toUpperCase()}`
      });
    } catch (errorObject) {
      showStatus(errorObject.message || "The CV could not be created.");
      window.toolboxAnalytics?.track("tool_error", { error_category: "processing" });
    } finally {
      setBusy(false);
    }
  });

  const loadFonts = cv => {
    const loadFamily = family => {
      if (fontPromises.has(family)) return fontPromises.get(family);
      const promise = Promise.all(FONT_URLS[family].map(url => fetch(url).then(response => {
        if (!response.ok) throw new Error("A CV font could not be loaded.");
        return response.arrayBuffer();
      }))).then(([regular, bold]) => ({ regular, bold }));
      fontPromises.set(family, promise);
      return promise;
    };
    const families = cvFontFamilies(cv);
    return Promise.all(families.map(loadFamily))
      .then(fonts => Object.fromEntries(families.map((family, index) => [family, fonts[index]])));
  };

  window.addEventListener("pagehide", () => operationResult.dispose());
  renumberEntries(experienceEntries, "[data-experience-entry]");
  renumberEntries(educationEntries, "[data-education-entry]");
  update();
}

function value(id) {
  return document.getElementById(id)?.value || "";
}

function collectEntries(container, selector) {
  return Array.from(container.querySelectorAll(selector)).map(entry =>
    Object.fromEntries(Array.from(entry.querySelectorAll("[data-field]")).map(field => [
      field.dataset.field,
      field.type === "checkbox" ? field.checked : field.value
    ])));
}

function renumberEntries(container, selector) {
  const type = selector.includes("experience") ? "Role" : "Education";
  const entries = Array.from(container.querySelectorAll(selector));
  entries.forEach((entry, index) => {
    const number = index + 1;
    entry.querySelector("h3").textContent = `${type} ${number}`;
    const remove = entry.querySelector("[data-remove-entry]");
    remove.setAttribute("aria-label", `Remove ${type.toLocaleLowerCase()} ${number}`);
    const moveGroup = entry.querySelector(".cv-move-actions");
    moveGroup.setAttribute("aria-label", `Reorder ${type.toLocaleLowerCase()} ${number}`);
    for (const button of moveGroup.querySelectorAll("[data-move-entry]")) {
      const direction = button.dataset.moveEntry;
      button.setAttribute("aria-label", `Move ${type.toLocaleLowerCase()} ${number} ${direction}`);
      button.disabled = direction === "up" ? index === 0 : index === entries.length - 1;
    }
  });
}

function renderPreview(container, blocks) {
  if (!blocks.length) {
    const placeholder = document.createElement("div");
    placeholder.className = "cv-preview-placeholder";
    placeholder.textContent = "Your ATS-friendly CV preview will appear here as you type.";
    container.replaceChildren(placeholder);
    return;
  }
  container.replaceChildren(...blocks.map(block => {
    if (block.type === "entry-meta") {
      const row = document.createElement("p");
      row.className = "cv-paper-entry-meta";
      row.textContent = [block.left, block.right].filter(Boolean).join(" | ");
      if (isRtlText(row.textContent)) row.dir = "rtl";
      return row;
    }
    const tag = {
      name: "div",
      "target-title": "div",
      contact: "p",
      section: "h3",
      "entry-title": "h4",
      paragraph: "p",
      bullet: "p"
    }[block.type] || "p";
    const element = document.createElement(tag);
    element.className = `cv-paper-${block.type}`;
    element.textContent = block.text;
    if (isRtlText(block.text)) element.dir = "rtl";
    if (block.type === "name") element.setAttribute("role", "heading");
    return element;
  }));
}

function renderChecks(container, scoreElement, analysis) {
  scoreElement.querySelector("[data-cv-score-value]").textContent = String(analysis.score);
  scoreElement.style.setProperty("--score", analysis.score);
  scoreElement.setAttribute("aria-label", `ATS readiness score ${analysis.score} out of 100`);
  container.replaceChildren(...analysis.checks.map(check => {
    const item = document.createElement("li");
    item.classList.toggle("is-passed", check.passed);
    const copy = document.createElement("div");
    const title = document.createElement("strong");
    title.textContent = check.label;
    copy.append(title);
    if (!check.passed) {
      const recommendation = document.createElement("span");
      recommendation.textContent = check.recommendation;
      copy.append(recommendation);
    }
    item.append(copy);
    return item;
  }));
}

function renderKeywords(container, result) {
  if (!result.hasJobDescription) {
    const empty = document.createElement("p");
    empty.className = "cv-keyword-empty";
    empty.textContent = "Paste a job description to see its recurring terms and which ones already appear in your CV.";
    container.replaceChildren(empty);
    return;
  }
  if (!result.keywords.length) {
    const empty = document.createElement("p");
    empty.className = "cv-keyword-empty";
    empty.textContent = "No useful recurring terms were found. Try pasting the complete job description.";
    container.replaceChildren(empty);
    return;
  }
  const summary = document.createElement("p");
  summary.className = "cv-keyword-summary";
  summary.append(document.createTextNode(`${result.matched.length} of ${result.keywords.length} important terms found`));
  const percent = document.createElement("strong");
  percent.textContent = `${result.percent}%`;
  summary.append(percent);
  container.replaceChildren(
    summary,
    keywordGroup("Found in your CV", result.matched, false),
    keywordGroup("Review if genuinely relevant", result.missing, true)
  );
}

function keywordGroup(label, keywords, missing) {
  const group = document.createElement("div");
  group.className = "cv-keyword-group";
  const heading = document.createElement("span");
  heading.textContent = label;
  const chips = document.createElement("div");
  chips.className = "cv-keyword-chips";
  for (const keyword of keywords) {
    const chip = document.createElement("span");
    chip.className = `cv-keyword-chip${missing ? " is-missing" : ""}`;
    chip.textContent = keyword;
    chips.append(chip);
  }
  if (!keywords.length) {
    const chip = document.createElement("span");
    chip.className = "cv-keyword-chip";
    chip.textContent = missing ? "None" : "Add truthful matching terms";
    chips.append(chip);
  }
  group.append(heading, chips);
  return group;
}
